import './datasheet.css';
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, message, Space, Switch, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import {
  DataSheetContext,
  DataSheetPagerContext, DataSheetPagerProps,
  DataSheetSharedProps,
} from './context';
import { getDOMRectsByClassName } from './domrect_utils';
import { ColumnInfo, ColumnValue, GridElement, RowValue, Value } from './types';
import { CellRenderer } from './cell_renderer';
import { createHeader, HeaderColumn } from './header';
import { cssList } from '../../utils/csslist';
import { DataSheetPager } from './pager';
import DataSheet from './datasheet';
import { Header } from '../../model/spread_sheet_response';
import { DateTypeKeys, SourceKeys } from '../../constant';
import { buildCsvUpdateRequest, CsvUpdateResponse } from '../../model/csvupadte_request';
import { useSpreadSheetStore } from './spreadsheet_store';
import { useIndicator } from '../../hook/use_indicator';
import { ConflictModal } from './conflict_modal';
import { ErrorModal } from './error_modal';
import { Messages } from '../../constant/messages';
import { useTaskEditStore } from '../../index/pages/task_edit/task_edit_store';
import { CsvListRequest } from '../../model/csvlist_request';


type RenderFunc = () => React.ReactNode;
interface SpreadSheetProps {
  historyId?: number;
  initialRows: RowValue[];
  targetFile: SourceKeys;
  dateType: DateTypeKeys;
  page?: number;
  count: number;
  year: number;
  month: number;
  headers: Header[];
  resultCaption?: string;
  onCellsChanged?: (rows: GridElement[][]) => void;
  parPage?: number;
  freezeX?: number;
  toolTip: React.ReactNode | RenderFunc;
  lastModified: dayjs.Dayjs;
  showId: boolean;
  onUpdate?: (upated: RowValue[], page: number) => void;
  editable?: boolean;
  receiveApi?: CsvListRequest;
}

type ColumnInfoMap = { [key: string]: ColumnInfo };
const idWidth = 50;

const convertColumnInfoMap = (columns: ColumnInfo[], showId: boolean): ColumnInfoMap => {
  const map: ColumnInfoMap = {};
  columns.forEach((c) => {
    map[c.key] = c;
  });
  if (showId) {
    map.id = { key: 'id', type: 'header', width: idWidth };
  }
  return map;
};

const initialColumnValue = (rowId: number | string | undefined, historyId: number | undefined, column: ColumnValue, metaData: ColumnInfoMap, isNew: boolean): GridElement => {
  return {
    rowId,
    historyId,
    isNew,
    readOnly: metaData[column.key]?.type === 'header' || metaData[column.key]?.type === 'group' || metaData[column.key]?.type === 'readonly',
    type: metaData[column.key]?.type ?? 'text',
    columnKey: column.key,
    value: column.value,
    metaData: column.metaData,
    width: metaData[column.key]?.width,
    isEdit: (metaData[column.key]?.type === 'header' || metaData[column.key]?.type === 'group' || metaData[column.key]?.type === 'readonly') ? false : isNew,
    beforeValue: column.value,
  };
};

const toRowData = (rows: GridElement[][]): RowValue[] => {
  return rows.map((r) => {
    if (r.length === 0) {
      return null;
    }
    const { rowId } = r[0];
    return {
      rowId,
      columns: r.map((c) => {
        return {
          value: c.value,
          historyId: c.historyId,
          metaData: c.metaData,
          key: c.columnKey,
        };
      }),
    };
  }).filter((r) => r);
};


const initialRowValue = (row: RowValue, columns: ColumnInfoMap, showId: boolean): GridElement[] => {
  const cols = row.columns.map((c) => {
    return initialColumnValue(row.rowId, row.historyId, c, columns, false);
  });
  if (!showId) {
    return cols;
  }
  return [{ rowId: row.rowId, historyId: row.historyId, columnKey: 'id', value: row.rowId, isNew: false, readOnly: true, type: 'header', width: idWidth }, ...cols];
};

function update(rows: GridElement[][], cell: GridElement, value: Value): GridElement[][] {
  return rows.map((r) => r.map((c) => {
    if (c.rowId === cell.rowId && c.columnKey === cell.columnKey) {
      return { ...c, beforeValue: cell.beforeValue ?? cell.value, value, isEdit: cell.beforeValue !== value };
    }
    return c;
  }));
}

type SlicedRow = {
  before: GridElement[][],
  current: GridElement[][],
  after: GridElement[][],
};

const sliceRows = (rows: GridElement[][], pager: DataSheetPagerProps): SlicedRow => {
  const prevIndex = pager.page < 1 ? 0 : (pager.page - 1) * pager.parPage;
  const start = (pager.page - 1) * pager.parPage;

  return {
    before: rows.slice(0, prevIndex),
    current: rows.slice(start, start + pager.parPage),
    after: rows.slice(start + pager.parPage),
  };
};

const buildColumnInfo = (column: Header): ColumnInfo[] => {
  if (column.type === 'group') {
    return column.nested.flatMap((c) => buildColumnInfo(c));
  }
  return [{ key: column.key, type: column.type, width: column.width }];
};
const buildColumnInfoMap = (columns: Header[], showId: boolean): ColumnInfoMap => {
  const infos = columns.flatMap((h) => {
    return buildColumnInfo(h);
  });
  return convertColumnInfoMap(infos, showId);
};

export const SpreadSheet: React.FC<SpreadSheetProps> = ({ initialRows, page = 1, receiveApi, count, headers, onCellsChanged, parPage = 20, freezeX, showId, toolTip, lastModified, onUpdate, editable = true, resultCaption = '検索結果', ...props }) => {
  const ref = useRef<HTMLDivElement>(null);
  const isClicked = useRef(false);
  const isEditing = useRef(false);
  const selected = useRef<HTMLTableCellElement[]>([]);
  const scrollIntervalId = useRef<NodeJS.Timeout>(null);

  const [newId, setNewId] = useState<number>(0);
  const [editMode, setEditMode] = useState(false);
  const height = 30;

  const [showHeaders, setShowHeaders] = useState<Header[]>(headers);
  const columnInfos = useMemo(() => { return buildColumnInfoMap(headers, showId); }, [headers, showId]);
  const r = useMemo(() => {
    return initialRows.map((r) => initialRowValue(r, columnInfos, showId).map((c, index) => ({ ...c, freezeX: freezeX && index < freezeX })));
  }, [initialRows, columnInfos, showId]);
  const [rows, setRows] = useState<GridElement[][]>(r);
  const { getCsvList } = useTaskEditStore((selector) => selector);
  const indicator = useIndicator();
  const navi = useNavigate();

  const [currentPage, setCurrentPage] = useState<number>(page);

  const [pager, setPager] = useState<DataSheetPagerProps>({
    page,
    parPage,
    setPage: (page) => {
      setPager((p) => {
        return { ...p, page };
      });
    },
    total: count,
  });

  useEffect(() => {
    // console.log('setPagerCurrentPage');
    setPager((p) => {
      return { ...p, page: currentPage };
    });
  }, [currentPage]);


  const pagerRef = useRef(pager);

  const sliced = useMemo(() => {
    // console.log('sliced');
    if (receiveApi) {
      return {
        before: [],
        current: rows,
        after: [],
      };
    }
    return sliceRows(rows, pager);
  }, [rows, pager, receiveApi]);

  const [prevRows, setPrevRows] = useState<GridElement[][]>(sliced.before);
  const [displayRows, setDisplayRows] = useState<GridElement[][]>(sliced.current);
  const [afterRows, setAfterRows] = useState<GridElement[][]>(sliced.after);
  const [sharedData, setSharedData] = useState<DataSheetSharedProps>({ parent: ref });
  const [updateResponse, setUpdateResponse] = useState<CsvUpdateResponse>(undefined);

  const spreadSheetStore = useSpreadSheetStore((e) => e);

  useEffect(() => {
    // console.log('setShowHeaders');
    setShowHeaders(headers);
  }, [headers]);

  const changeEditMode = useCallback((edit: boolean) => {
    // console.log('changeEditMode');
    if (edit) {
      setEditMode(true);
      return;
    }
    if (receiveApi) {
      setDisplayRows(rows);
    } else {
      const sliced = sliceRows(rows, pager);
      setPrevRows(sliced.before);
      setDisplayRows(sliced.current);
      setAfterRows(sliced.after);
    }
    setEditMode(false);
  }, [rows, receiveApi]);

  useEffect(() => {
    if (receiveApi) {
      return;
    }
    // console.log('changeDiff');
    const diff = pager.page - pagerRef.current.page;
    pagerRef.current = pager;

    if (diff === 0) {
      // 変化なし
      return;
    }
    let [prev, current, after] = [prevRows, displayRows, afterRows];

    if (diff < 0) {
      // 前のページに遷移
      for (let i = -1; i >= diff; i--) {
        after = current.concat(after);
        current = prev.length < pager.parPage ? [...prev] : prev.slice(-pager.parPage);
        prev = prev.length < pager.parPage ? [] : prev.slice(0, prev.length - pager.parPage);
      }
    }
    if (diff > 0) {
      // 次のページに遷移
      for (let i = 1; i <= diff; i++) {
        prev = prev.concat(current);
        current = after.length < pager.parPage ? [...after] : after.slice(0, pager.parPage);
        after = after.length < pager.parPage ? [] : after.slice(pager.parPage);
      }
    }

    setPrevRows(prev);
    setDisplayRows(current);
    setAfterRows(after);
  }, [pager.page, pager.parPage, receiveApi]);

  const onChangePager = useCallback((page: number) => {
    // console.log('onChangePager');
    if (receiveApi) {
      const request: CsvListRequest = {
        ...receiveApi,
        page,
      };
      indicator.show();
      getCsvList(request).then((response) => {
        setShowHeaders(response.headers);
        setPager((p) => {
          return { ...p, total: response.count };
        });
        const rows = response.rows.map((r) => initialRowValue(r, columnInfos, showId).map((c, index) => ({ ...c, freezeX: freezeX && index < freezeX })));
        setCurrentPage(page);
        setRows(rows);
        setPrevRows([]);
        setDisplayRows(rows);
        setAfterRows([]);
      }).catch((e) => {
        const { status } = e.response;
        if (status === 401 || status === 403) {
          window.alert(Messages.ERR_DIALOG_RETURN_LOGIN);
          navi('/login', { state: { message: Messages.ERR_401 } });
          return;
        }
        if (status === -1) {
          window.alert(Messages.ERR_DIALOG_TIMEOUT);
          return;
        }
        if (status === 409) {
          window.alert(Messages.ERR_DIALOG_CSV_CONFLICT);
          return;
        }
        window.alert(Messages.ERR_DIALOG_RETRY_OPERATION);
      }).finally(() => {
        indicator.hide();
      });
      return;
    }
    setCurrentPage(page);
  }, [receiveApi]);

  useEffect(() => {
    // console.log('changeOnChangePager');
    setPager((p) => {
      return { ...p, onChange: onChangePager };
    });
  }, [onChangePager]);

  const buildNested = (column: Header): HeaderColumn[] => {
    if (column.type !== 'group' || column.nested === undefined) {
      return undefined;
    }
    return column.nested.map((c) => {
      const nest: HeaderColumn = {
        label: c.name,
        nested: buildNested(c),
      };
      return nest;
    });
  };

  const headerColumns = useMemo(() => {
    // console.log('headerColumns');
    const header = showHeaders.map((h) => {
      const header: HeaderColumn = {
        label: h.name,
        width: h.width,
        nested: buildNested(h),
      };
      return header;
    });
    if (showId) {
      return [{ label: 'ID' }, ...header];
    }
    return header;
  }, [showHeaders, showId]);


  const hds = useMemo(() => {
    // console.log('hds');
    return createHeader(headerColumns, height, freezeX);
  }, [headerColumns, height, freezeX]);

  useEffect(() => {
    // console.log('changeMouseEvent');
    const onMouseDown = () => {
      isClicked.current = true;
    };

    const onMouseUp = () => {
      isClicked.current = false;
      if (scrollIntervalId.current) {
        clearInterval(scrollIntervalId.current);
        scrollIntervalId.current = null;
      }
    };

    const handler = (e: MouseEvent) => {
      if (isClicked.current) {
        const hasFreezeX = selected.current.find((s) => s.classList.contains('freezeX'));
        const hasFreezeY = selected.current.find((s) => s.classList.contains('freezeY'));
        const hasNotFreeze = selected.current.find((s) => s.classList.contains('freeze') === false);

        if (hasFreezeX && hasNotFreeze && ref.current.scrollLeft > 0) {
          ref.current.scrollLeft = 0;
        }
        if (hasFreezeY && hasNotFreeze && ref.current.scrollTop > 0) {
          ref.current.scrollTop = 0;
        }

        const bounce = ref.current.getBoundingClientRect();
        const clear = () => {
          if (scrollIntervalId.current) {
            clearInterval(scrollIntervalId.current);
            scrollIntervalId.current = null;
          }
        };
        if (e.clientX < bounce.left && ref.current.scrollLeft > 0) {
          if (scrollIntervalId.current == null) {
            scrollIntervalId.current = setInterval(() => {
              ref.current.scrollLeft -= 100;
              if (ref.current.scrollLeft <= 0) {
                clear();
              }
            }, 100);
          }
        } else if (e.clientX > bounce.right && ref.current.scrollLeft < ref.current.scrollWidth) {
          if (scrollIntervalId.current == null) {
            scrollIntervalId.current = setInterval(() => {
              ref.current.scrollLeft += 100;
              if (ref.current.scrollLeft >= ref.current.scrollWidth) {
                clear();
              }
            }, 100);
          }
        } else if (e.clientY > bounce.bottom && ref.current.scrollTop < ref.current.scrollHeight) {
          if (scrollIntervalId.current == null) {
            scrollIntervalId.current = setInterval(() => {
              ref.current.scrollTop += 100;
              if (ref.current.scrollTop >= ref.current.scrollHeight) {
                clear();
              }
            }, 100);
          }
        } else if (e.clientY < bounce.top && ref.current.scrollTop > 0) {
          if (scrollIntervalId.current == null) {
            scrollIntervalId.current = setInterval(() => {
              ref.current.scrollTop -= 100;
              if (ref.current.scrollTop <= 0) {
                clear();
              }
            }, 100);
          }
        } else {
          clear();
        }
      }
    };
    const keyHandler = (e: KeyboardEvent) => {
      if (isEditing.current) {
        return;
      }
      if (!(e.key === 'ArrowRight' || e.key === 'ArrowLeft' || e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        return;
      }
      if (selected.current.length === 0) {
        return;
      }
      // TODO: 先頭のセルだけで今は見ているが、それぞれのケースで実際は動きを確認するほうが良い
      const select = selected.current[0];
      const bounds = select.getBoundingClientRect();
      const table = ref.current.getBoundingClientRect();

      if (e.key === 'ArrowRight') {
        if (table.right <= (bounds.right + 1)) {
          ref.current.scrollLeft += (bounds.right - (table.right)) + 15;
        }
      }
      if (e.key === 'ArrowLeft') {
        // eslint-disable-next-line @typescript-eslint/no-shadow
        const freezeX = getDOMRectsByClassName('freezeX');
        if (freezeX.length === 0) {
          if (table.left >= bounds.left - 1) {
            ref.current.scrollLeft -= (table.left - bounds.left);
          }
          return;
        }
        const isFreeze = freezeX.find((f) => {
          return f.right >= bounds.left;
        });
        if (isFreeze) {
          // 隠れてる部分だけ見せる
          ref.current.scrollLeft -= (isFreeze.right - bounds.left);
        }
      }
      if (e.key === 'ArrowUp') {
        const freezeY = getDOMRectsByClassName('freezeY');
        if (freezeY.length === 0) {
          if (table.top >= bounds.top - 1) {
            ref.current.scrollTop -= (table.top - bounds.top);
          }
          return;
        }
        const isFreeze = freezeY.find((f) => {
          return f.bottom >= bounds.top;
        });
        if (isFreeze) {
          // 隠れてる部分だけ見せる
          ref.current.scrollTop -= (isFreeze.bottom - bounds.top);
        }
      }
      if (e.key === 'ArrowDown') {
        if (table.bottom <= bounds.bottom + 1) {
          // 15 = スクロールバーの大きさ
          ref.current.scrollTop += (bounds.bottom - (table.bottom)) + 15;
        }
      }
    };

    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('mousemove', handler);
    window.addEventListener('keydown', keyHandler);

    return () => {
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('mousemove', handler);
      window.removeEventListener('keydown', keyHandler);
    };
  }, []);

  useEffect(() => {
    // console.log('setSharedData');
    if (ref.current) {
      setSharedData({
        ...sharedData,
        parent: ref,
        left: ref.current.getBoundingClientRect().left,
        top: ref.current.getBoundingClientRect().top,
      });
    }
  }, [ref.current]);


  const editModeChanged = (editing: boolean) => {
    isEditing.current = editing;
  };

  const renderSheet = useCallback((sheetData: GridElement[][], edit: boolean): ReactNode => {
    return (
        <DataSheet
            className="datasheet"
            overflow="wrap"
            readonly={!edit}
            data={sheetData}
            valueRenderer={(cell) => cell.value?.toString() ?? ''}
            onCellsChanged={(changes) => {
              changes.forEach(({ cell, value }) => {
                setDisplayRows((r) => update(r, cell, value));
              });
            }}
            onSelect={() => {
              const selecteds = document.getElementsByClassName('selected');
              const st: HTMLTableCellElement[] = [];
              for (let i = 0; i < selecteds.length; i++) {
                st.push(selecteds.item(i) as HTMLTableCellElement);
              }
              selected.current = st;
            }}
            editModeChanged={editModeChanged}
            handleCopy={({ dataRenderer, valueRenderer, data, start, end, range }) => {
              // eslint-disable-next-line @typescript-eslint/no-shadow
              const rows = range(start.i, end.i)
                .map((i) => range(start.j, end.j).map((j) => {
                  const cell = data[i][j];
                  const value = dataRenderer ? dataRenderer(cell, i, j) : null;
                  const txt = (() => {
                    if (value === '' || value === null || typeof value === 'undefined') {
                      return valueRenderer(cell, i, j);
                    }
                    return value;
                  })();
                  if (typeof txt === 'string') {
                    if (txt.includes('\n')) {
                      return '"' + txt.replace(/\r\n/g, '\n') + '"';
                    }
                  }
                  return txt;
                }).join('\t'));
              const text = rows.join('\r\n');
              navigator.clipboard.writeText(text);
            }}
            cellRenderer={CellRenderer}
            parsePaste={(str) => {
              const lines = str.split('\r\n');
              const result = lines.map((line) => line.split('\t'));
              if (result[result.length - 1].length === 1 && result[result.length - 1][0] === '') {
                result.pop();
              }
              return result.map((row) => row.map((cell) => (cell.startsWith('"') && cell.endsWith('"') && cell.includes('\n') ? cell.slice(1, -1) : cell)));
            }
            }
        />
    );
  }, []);

  const onAdd = useCallback(() => {
    // console.log('onAdd');
    const allHeaders = headers.flatMap((h) => {
      return h.nested?.length > 0 ? h.nested.map((n) => n.key) : h.key;
    });
    const newRow = allHeaders.map((c) => {
      return initialColumnValue(newId, props.historyId, { key: c, value: '', metaData: {} }, columnInfos, true);
    });
    if (!showId) {
      setDisplayRows((r) => [newRow, ...r]);
    }
    const id: GridElement = { rowId: newId, historyId: props.historyId, columnKey: 'id', value: '新', isNew: false, beforeValue: '', readOnly: true, type: 'header', width: idWidth };
    const newRowWithId = [id, ...newRow];
    setDisplayRows((r) => [newRowWithId, ...r]);
    setNewId((id) => id - 1);
  }, [displayRows, columnInfos, props.historyId, headers, showId, newId]);

  const sheet = useMemo(() => {
    // console.log('sheet');
    if (!ref.current) {
      return null;
    }
    return renderSheet([...hds, ...displayRows], editMode);
  }, [ref.current, hds, displayRows, editMode]);

  const modifiedRows = (useMemo(() => {
    return displayRows.map((r) => {
      return r.filter((c) => c.isEdit);
    }).filter((r) => r.length > 0);
  }, [displayRows]));

  const handleUpdate = useCallback((forceUpdate: boolean) => {
    // console.log('handleUpdate');
    const request = buildCsvUpdateRequest({ year: props.year, month: props.month, targetFile: props.targetFile, dateType: props.dateType, rows: [] }, modifiedRows);

    setUpdateResponse(undefined);
    indicator.show();
    spreadSheetStore.update(request, forceUpdate).then((response) => {
      setUpdateResponse(response);
      if (response.hasError || response.hasWarning) {
        return;
      }
      onUpdate?.(toRowData(modifiedRows), pager.page);
    }).catch((e) => {
      const { status } = e.response;
      if (status === 401 || status === 403) {
        window.alert(Messages.ERR_DIALOG_RETURN_LOGIN);
        navi('/login', { state: { message: Messages.ERR_401 } });
        return;
      }
      if (status === -1) {
        window.alert(Messages.ERR_DIALOG_TIMEOUT);
        return;
      }
      if (status === 409) {
        window.alert(Messages.ERR_DIALOG_UPDATE_CSV_CONFLICT);
        return;
      }
      if (status === 400) {
        window.alert(Messages.ERR_INVALID_INPUT);
        return;
      }
      window.alert(Messages.ERR_DIALOG_RETRY_OPERATION);
    }).finally(() => {
      indicator.hide();
    });
  }, [pager, modifiedRows, onUpdate]);

  return (
      <>
      <DataSheetPagerContext.Provider value={pager}>
      <ErrorModal show={updateResponse?.hasError ?? false} response={updateResponse}/>
      <ConflictModal show={updateResponse?.hasWarning ?? false} onOk={() => handleUpdate(true)} response={updateResponse}/>
      <div className="planeContent">
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div>
          <Space>
            <div>
              {resultCaption ?? '検索結果'} {pager.total}件&nbsp;&nbsp;
            </div>
            <DataSheetPager isDurty={editMode}/>
            { toolTip
            && <Tooltip title={toolTip}>
              <Button>凡例</Button>
            </Tooltip>
            }
          </Space>
          </div>
          <div>
            { editable && <Space>
              <div>
                <label><Switch value={editMode} onChange={changeEditMode} /> 編集モード</label>&nbsp;
              </div>
              {
                props.historyId && <Button disabled={!editMode} type={'primary'} onClick={onAdd}>追加</Button>
              }
              <Button disabled={!editMode || modifiedRows.length === 0} loading={spreadSheetStore.updating} type="primary" onClick={() => handleUpdate(false)}>更新</Button>
            </Space>
            }
          </div>
        </div>
      </div>
      <div ref={ref} className={cssList('datasheet-scroll')} >
        <DataSheetContext.Provider value={sharedData}>
          {sheet}
        </DataSheetContext.Provider>
      </div>
      <div className={'datasheet-last-modify'}>
        最新データ取得日時：{lastModified.format('YYYY/MM/DD HH:mm:ss')}
      </div>
      </DataSheetPagerContext.Provider>
      </>
  );
};
